import React, { useCallback, useMemo } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import {
  Select as MuiSelect,
  OutlinedInput,
  Checkbox as MuiCheckbox,
  ListItemText,
  MenuItem,
  Box,
  Chip,
  ListSubheader,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { CountryCode } from "../../../../../utils/CountryCode";
import { getWorkPackagesSagaAction } from "../../../../../redux/saga/external-contract/saga-actions";
import { WorkPackagesType } from "../../../../../redux/slices/external-contract/Types";
import { Constants } from "../../../../../utils/constants";
import {
  getPartnerVerticalSagaAction,
  getPartnerTierSagaAction,
  getRevenueStreamSagaAction,
  getPartnerTypesSagaAction,
} from "../../../../../redux/saga/partner-details/saga-actions";
import "./PartnerFilterModal.scss";

interface PartnerFilterModalProps {
  partnerFilterProp: {
    partnerName: string[];
    status: string[];
    type: string[];
    tier: string[];
    vertical: string[];
    revenueStream: string[];
    accountManager: string[];
    region: string[];
    country: string[];
    contractName: string[];
    contractStatus: string[];
    startDateRange: string[];
    endDateRange: string[];
  };
  setPartnerFilterProp: React.Dispatch<React.SetStateAction<any>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Option {
  label: string;
  value: string;
}

interface GroupedOptions {
  label: string;
  value: string;
  options?: Option[];
}

interface DateState {
  startDateFrom: Date | null;
  startDateTo: Date | null;
  endDateFrom: Date | null;
  endDateTo: Date | null;
}

// Static data - no need for memoization since CountryCode is static
const CountryOptions = [
  { label: "Global", value: "Global" },
  ...CountryCode.map((country) => ({
    label: country.name,
    value: country.name,
  })),
];

const transformWorkPackages = (
  workPackages: WorkPackagesType[]
): GroupedOptions[] => {
  const grouped: Record<string, Option[]> = workPackages.reduce(
    (acc: Record<string, Option[]>, pkg) => {
      const description = pkg.description.trim();
      if (!acc[description]) {
        acc[description] = [];
      }
      acc[description].push({
        label: pkg.value.trim(),
        value: pkg.value.trim(),
      });
      return acc;
    },
    {}
  );

  return Object.keys(grouped).map((key) => ({
    label: key,
    value: key,
    options: grouped[key],
  }));
};

const PartnerFilterModal: React.FC<PartnerFilterModalProps> = ({
  partnerFilterProp,
  setPartnerFilterProp,
  setModalOpen,
}) => {
  const dispatch = useAppDispatch();

  const {
    partnerTypes,
    partnerTier,
    partnerVertical,
    allPartners,
    revenueStream,
  } = useAppSelector((state) => state.partner_details);
  const { workPackages } = useAppSelector((state) => state.external_contract);

  const [partnerFilter, setPartnerFilter] = React.useState(partnerFilterProp);
  const [dates, setDates] = React.useState<DateState>(() => {
    const dateObject = (date: string | undefined) =>
      date ? new Date(date) : null;
    return {
      startDateFrom: dateObject(partnerFilterProp.startDateRange[0]),
      startDateTo: dateObject(partnerFilterProp.startDateRange[1]),
      endDateFrom: dateObject(partnerFilterProp.endDateRange[0]),
      endDateTo: dateObject(partnerFilterProp.endDateRange[1]),
    };
  });

  const partnerStatus = useMemo(
    () =>
      Constants.PARTNER_STATUS.map((status) => {
        return {
          label: status,
          value: status,
        };
      }),
    []
  );

  const types = useMemo(
    () =>
      partnerTypes.map((type) => {
        return {
          label: type.value,
          value: type.value,
        };
      }),
    [partnerTypes]
  );

  const tiers = useMemo(
    () =>
      partnerTier.map((tier) => {
        return {
          label: tier.value,
          value: tier.value,
        };
      }),
    [partnerTier]
  );

  const verticals = useMemo(
    () =>
      partnerVertical.map((vertical) => {
        return {
          label: vertical.value,
          value: vertical.value,
        };
      }),
    [partnerVertical]
  );

  const accountManagerOptions = useMemo(
    () =>
      Array.from(
        new Set(allPartners.map((p) => p.account_manager).filter(Boolean))
      ).map((manager) => ({
        label: manager,
        value: manager,
      })),
    [allPartners]
  );

  const partnerOptions = useMemo(
    () =>
      allPartners.map((partner) => ({
        label: partner.partner_name,
        value: partner.partner_name,
      })),
    [allPartners]
  );

  const revenueStreamOptions = useMemo(
    () =>
      revenueStream.map((stream) => ({
        label: stream.value,
        value: stream.value,
      })),
    [revenueStream]
  );

  const workPackageTypeOptions = useMemo(
    () => transformWorkPackages(workPackages),
    [workPackages]
  );

  const selectedPartnerNames = useMemo(
    () =>
      partnerFilter.partnerName.map((name) => ({ label: name, value: name })),
    [partnerFilter.partnerName]
  );

  React.useEffect(() => {
    if (partnerTypes.length === 0) dispatch(getPartnerTypesSagaAction());
    if (workPackages.length === 0) dispatch(getWorkPackagesSagaAction());
    if (revenueStream.length === 0) dispatch(getRevenueStreamSagaAction());
    if (partnerTier.length === 0) dispatch(getPartnerTierSagaAction());
    if (partnerVertical.length === 0) dispatch(getPartnerVerticalSagaAction());
  }, [
    dispatch,
    partnerTypes,
    workPackages,
    revenueStream,
    partnerTier,
    partnerVertical,
  ]);

  const onCheckboxClick = useCallback(
    (
      eventResult: { checked: boolean; value: string },
      filterName: keyof typeof partnerFilter
    ) => {
      setPartnerFilter((prevState) => {
        const currentValues = prevState[filterName];

        if (eventResult.checked) {
          return currentValues.includes(eventResult.value)
            ? prevState
            : {
                ...prevState,
                [filterName]: [...currentValues, eventResult.value],
              };
        } else {
          return {
            ...prevState,
            [filterName]: currentValues.filter(
              (val) => val !== eventResult.value
            ),
          };
        }
      });
    },
    []
  );

  const formatDateForDB = useCallback((date: Date | null) => {
    return date ? date.toLocaleDateString("en-CA") : null;
  }, []);

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null, name: keyof DateState) => {
      const actualDate = Array.isArray(date) ? date[0] : date;

      setDates((prev) => ({ ...prev, [name]: actualDate }));

      const formattedDate = formatDateForDB(actualDate);

      setPartnerFilter((prev) => {
        const newFilter = { ...prev };

        switch (name) {
          case "startDateFrom":
            newFilter.startDateRange = [formattedDate, prev.startDateRange[1]];
            // If both dates are null, set to empty array
            if (!formattedDate && !prev.startDateRange[1]) {
              newFilter.startDateRange = [];
            }
            break;
          case "startDateTo":
            newFilter.startDateRange = [prev.startDateRange[0], formattedDate];
            // If both dates are null, set to empty array
            if (!prev.startDateRange[0] && !formattedDate) {
              newFilter.startDateRange = [];
            }
            break;
          case "endDateFrom":
            newFilter.endDateRange = [formattedDate, prev.endDateRange[1]];
            // If both dates are null, set to empty array
            if (!formattedDate && !prev.endDateRange[1]) {
              newFilter.endDateRange = [];
            }
            break;
          case "endDateTo":
            newFilter.endDateRange = [prev.endDateRange[0], formattedDate];
            // If both dates are null, set to empty array
            if (!prev.endDateRange[0] && !formattedDate) {
              newFilter.endDateRange = [];
            }
            break;
        }
        return newFilter;
      });
    },
    [formatDateForDB]
  );

  const onClearAllClick = useCallback(() => {
    const emptyFilter: typeof partnerFilterProp = {
      partnerName: [],
      status: [],
      type: [],
      tier: [],
      vertical: [],
      revenueStream: [],
      accountManager: [],
      region: [],
      country: [],
      contractName: [],
      contractStatus: [],
      startDateRange: [],
      endDateRange: [],
    };

    setPartnerFilter(emptyFilter);
    setDates({
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
    });
  }, []);

  const onApplyClick = useCallback(() => {
    let updatedFilter = { ...partnerFilter };

    // Handle partial date ranges
    if (updatedFilter.startDateRange[0] && !updatedFilter.startDateRange[1]) {
      updatedFilter.startDateRange[1] = updatedFilter.startDateRange[0];
    } else if (
      !updatedFilter.startDateRange[0] &&
      updatedFilter.startDateRange[1]
    ) {
      updatedFilter.startDateRange[0] = updatedFilter.startDateRange[1];
    }

    if (updatedFilter.endDateRange[0] && !updatedFilter.endDateRange[1]) {
      updatedFilter.endDateRange[1] = updatedFilter.endDateRange[0];
    } else if (
      !updatedFilter.endDateRange[0] &&
      updatedFilter.endDateRange[1]
    ) {
      updatedFilter.endDateRange[0] = updatedFilter.endDateRange[1];
    }
    setPartnerFilterProp(updatedFilter);
    setModalOpen(false);
  }, [partnerFilter, setPartnerFilterProp, setModalOpen]);

  const handleDropdownChange = useCallback(
    (data: Option[], filterName: keyof typeof partnerFilter) => {
      setPartnerFilter((prev) => ({
        ...prev,
        [filterName]: data.map((item) => item.value),
      }));
    },
    []
  );

  const handleMUISelectChange = useCallback(
    (data: string[], filterName: keyof typeof partnerFilter) => {
      setPartnerFilter((prev) => ({
        ...prev,
        [filterName]: data,
      }));
    },
    []
  );

  const toggleMUISelectOption = useCallback(
    (filterName: keyof typeof partnerFilter, optionValue: string) => {
      setPartnerFilter((prev) => {
        const current = prev[filterName] as string[];
        const exists = current.includes(optionValue);
        const updated = exists
          ? current.filter((v) => v !== optionValue)
          : [...current, optionValue];
        return { ...prev, [filterName]: updated } as typeof prev;
      });
    },
    []
  );

  return (
    <Modal
      headerText="Filter Partners"
      width={512}
      onClose={() => setModalOpen(false)}
      className="partner-filter-modal"
    >
      <div className="filters-container">
        <Dropdown
          id="partnerNames"
          label="Search or select Partners"
          placeholder="Partners"
          theme={dropDownThemes.LIGHT}
          options={partnerOptions}
          isClearable={true}
          isMulti={true}
          isSearchable={true}
          selected={selectedPartnerNames}
          onSelect={(data) => handleDropdownChange(data, "partnerName")}
        />

        <div className="gray-container partner-information-container">
          <div className="icon-label">
            <Iconography icon="building" size={IconographySizes.Medium} />
            <label>Partner Basic Information</label>
          </div>

          <label className="filter-label">Select Partner Status</label>
          <div className="values-wrapper">
            <MuiSelect
              id="partnerStatus"
              multiple
              value={partnerFilter.status}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "status")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Partner Status</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {partnerStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.status.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>

          <label className="filter-label">Select Partner Type</label>
          <div className="values-wrapper">
            <MuiSelect
              id="partnerType"
              multiple
              value={partnerFilter.type}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "type")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Partner Type</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {types.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.type.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>

          <label className="filter-label">Select Partner Tier</label>
          <div className="values-wrapper">
            <MuiSelect
              id="partnerTier"
              multiple
              value={partnerFilter.tier}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "tier")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Partner Tier</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {tiers.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.tier.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>

          <label className="filter-label">Select Partner Vertical</label>
          <div className="values-wrapper">
            <MuiSelect
              id="partnerVertical"
              multiple
              value={partnerFilter.vertical}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "vertical")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Partner Vertical</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {verticals.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.vertical.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>
        </div>

        <div className="gray-container">
          <div className="icon-label">
            <Iconography icon="imageUser" size={IconographySizes.Medium} />
            <label>HID Account Manager</label>
          </div>
          <label className="filter-label">Select HID Account Manager</label>
          <div className="values-wrapper">
            <MuiSelect
              id="accountManager"
              multiple
              value={partnerFilter.accountManager}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "accountManager")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select HID Account Manager</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {accountManagerOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.accountManager.includes(
                      option.value
                    )}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>
        </div>

        <div className="gray-container">
          <div className="icon-label">
            <Iconography icon="memo" size={IconographySizes.Medium} />
            <label>Revenue Stream</label>
          </div>
          <label className="filter-label">Select Revenue Stream</label>
          <div className="values-wrapper">
            <MuiSelect
              id="revenueStream"
              multiple
              value={partnerFilter.revenueStream}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "revenueStream")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Revenue Stream</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {revenueStreamOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.revenueStream.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>
        </div>

        <div className="gray-container">
          <div className="icon-label">
            <Iconography icon="globe" size={IconographySizes.Medium} />
            <label>Region & Country</label>
          </div>
          <div className="values-wrapper">
            {Constants.REGIONS.map((item) => (
              <div className="checkbox-wrapper" key={item}>
                <Checkbox
                  name={item}
                  value={item}
                  checked={partnerFilter.region.includes(item)}
                  id={item}
                  onChange={(e, eventResult) =>
                    onCheckboxClick(eventResult, "region")
                  }
                />
              </div>
            ))}
          </div>
          <Divider />
          <label className="filter-label">Select Country</label>
          <div className="values-wrapper">
            <MuiSelect
              id="country"
              multiple
              value={partnerFilter.country}
              onChange={(e: any) =>
                handleMUISelectChange(e.target.value, "country")
              }
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Country</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {CountryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <MuiCheckbox
                    checked={partnerFilter.country.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </div>
        </div>

        <div className="gray-container">
          <div className="icon-label">
            <Iconography icon="memo" size={IconographySizes.Medium} />
            <label>Contract Details</label>
          </div>
          <label className="filter-label">Contract Status</label>
          <div className="values-wrapper">
            {Constants.CONTRACT_STATUS.map((item) => (
              <div className="checkbox-wrapper" key={item}>
                <Checkbox
                  name={item}
                  value={item}
                  checked={partnerFilter.contractStatus.includes(item)}
                  id={item}
                  onChange={(e, eventResult) =>
                    onCheckboxClick(eventResult, "contractStatus")
                  }
                />
              </div>
            ))}
          </div>
          <Divider />
          <label className="filter-label">Select Work Package Type</label>
          <div className="values-wrapper">
            <MuiSelect
              id="contractName"
              multiple
              value={partnerFilter.contractName}
              onChange={(e: any) => {
                console.log(e.target.value);
              }}
              input={<OutlinedInput />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select Work Package Type</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                );
              }}
            >
              {workPackageTypeOptions.map((groupedOption) => (
                <React.Fragment key={groupedOption.value}>
                  <ListSubheader key={`${groupedOption.value}-subheader`}>
                    {groupedOption.label}
                  </ListSubheader>

                  {groupedOption.options.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      onClick={() =>
                        toggleMUISelectOption("contractName", option.value)
                      }
                    >
                      <MuiCheckbox
                        checked={partnerFilter.contractName.includes(
                          option.value
                        )}
                      />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </React.Fragment>
              ))}
            </MuiSelect>
          </div>
          <Divider />

          <div className="values-wrapper">
            <div className="date-range-container">
              <DatePicker
                label="Start Date From"
                onChange={(date) => handleDateChange(date, "startDateFrom")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="startDateFrom"
                name="startDateFrom"
                value={dates.startDateFrom}
                isClearable
              />
              <DatePicker
                label="Start Date To"
                onChange={(date) => handleDateChange(date, "startDateTo")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="startDateTo"
                name="startDateTo"
                value={dates.startDateTo}
                isClearable
              />
            </div>
            <div className="date-range-container">
              <DatePicker
                label="End Date From"
                onChange={(date) => handleDateChange(date, "endDateFrom")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="endDateFrom"
                name="endDateFrom"
                value={dates.endDateFrom}
                isClearable
              />
              <DatePicker
                label="End Date To"
                onChange={(date) => handleDateChange(date, "endDateTo")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="endDateTo"
                name="endDateTo"
                value={dates.endDateTo}
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="button-group">
          <div className="left">
            <Button
              variant="secondary"
              label="Cancel"
              onClick={() => setModalOpen(false)}
            />
          </div>
          <div className="right">
            <Button
              variant="secondary"
              label="Clear All"
              onClick={onClearAllClick}
            />
            <Button variant="primary" label="Apply" onClick={onApplyClick} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PartnerFilterModal;
